import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, TrendingUp, Target, ListChecks, Lightbulb, Package, Wrench, Users, ArrowRight, Star } from "lucide-react";

export const GrowthRoadmap = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/portal/growth-roadmap").then((r) => setData(r.data.roadmaps || [])).catch(() => setData([]));
  }, []);

  if (!data) return <div className="flex items-center gap-2 text-gray-400 py-10"><Loader2 className="animate-spin text-gold" size={18} /> Preparing your roadmap…</div>;

  return (
    <div data-testid="growth-roadmap">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold"><TrendingUp size={20} /></span>
        <h1 className="font-heading text-3xl text-white">Business Growth Roadmap</h1>
      </div>
      <p className="text-gray-500 text-sm mb-8">Your personalized, expert-guided plan to grow — updated as your project evolves.</p>

      {data.length === 0 ? (
        <div className="bg-navy-800 border border-white/5 p-10 text-center">
          <p className="text-gray-400 mb-4">Your growth roadmap is being prepared. Start a project to receive your personalized plan.</p>
          <Link to="/start" className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">Start a Project <ArrowRight size={16} /></Link>
        </div>
      ) : (
        <div className="space-y-8">
          {data.map((r) => (
            <div key={r.project_id} className="bg-navy-800 border border-white/5 p-6 md:p-8" data-testid={`roadmap-${r.project_id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-gold mb-1">{r.org_name} · {r.stage}</div>
                  <h2 className="font-heading text-2xl text-white">{r.headline || "Your Growth Plan"}</h2>
                </div>
                {r.confidence != null && (
                  <div className="text-right"><div className="font-heading text-lg text-gold">{Math.round(r.confidence * 100)}%</div><div className="text-[10px] text-gray-500">plan confidence</div></div>
                )}
              </div>
              {r.overview && <p className="text-gray-400 mb-6">{r.overview}</p>}

              {r.goals?.length > 0 && (
                <Section icon={Target} title="Current Goals">
                  <div className="flex flex-wrap gap-2">{r.goals.map((g, i) => <span key={i} className="text-sm bg-navy border border-white/10 text-gray-300 px-3 py-1.5">{g}</span>)}</div>
                </Section>
              )}

              {r.recommended_actions?.length > 0 && (
                <Section icon={ListChecks} title="Recommended Actions">
                  <ol className="space-y-3" data-testid={`roadmap-actions-${r.project_id}`}>
                    {r.recommended_actions.map((a, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="shrink-0 w-6 h-6 flex items-center justify-center border border-gold/40 text-gold text-xs font-heading">{i + 1}</span>
                        <div><div className="text-white text-sm font-medium">{a.title}</div>{a.detail && <div className="text-gray-400 text-sm mt-0.5">{a.detail}</div>}</div>
                      </li>
                    ))}
                  </ol>
                </Section>
              )}

              {r.future_opportunities?.length > 0 && (
                <Section icon={Lightbulb} title="Future Opportunities">
                  <ul className="space-y-1.5">{r.future_opportunities.map((o, i) => <li key={i} className="text-gray-300 text-sm flex gap-2"><span className="text-gold">·</span>{o}</li>)}</ul>
                </Section>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {r.suggested_services?.length > 0 && (
                  <Section icon={Wrench} title="Suggested Services">
                    <ul className="space-y-1.5">{r.suggested_services.map((s, i) => <li key={i} className="text-gray-300 text-sm flex gap-2"><span className="text-gold">·</span>{s}</li>)}</ul>
                  </Section>
                )}
                {r.suggested_strategists?.length > 0 && (
                  <Section icon={Users} title="Your Strategists">
                    <div className="flex flex-wrap gap-2">{r.suggested_strategists.map((s, i) => <span key={i} className="text-xs bg-gold/10 border border-gold/25 text-gold px-3 py-1.5">{s}</span>)}</div>
                  </Section>
                )}
              </div>

              {r.suggested_templates?.length > 0 && (
                <Section icon={Package} title="Suggested Designs">
                  <div className="grid sm:grid-cols-3 gap-4">
                    {r.suggested_templates.map((t) => (
                      <Link key={t.id} to={`/template/${t.slug || t.id}`} className="group block bg-navy border border-white/10 hover:border-gold/50 transition-colors overflow-hidden">
                        <div className="aspect-[4/3] bg-navy-800 overflow-hidden">
                          {t.preview_image ? <img src={t.preview_image} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">{t.category}</div>}
                        </div>
                        <div className="p-3"><div className="text-white text-sm truncate">{t.title}</div><div className="flex items-center justify-between mt-1"><span className="text-gold text-xs uppercase tracking-wide">{t.category || "Design"}</span><span className="flex items-center gap-1 text-gray-400 text-xs"><Star size={11} className="text-gold fill-gold" /> {t.rating}</span></div></div>
                      </Link>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-6 last:mb-0">
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold mb-3"><Icon size={14} /> {title}</div>
    {children}
  </div>
);
